const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
const cardSelector = '.neon-storefront__agents-banner[data-automnia-parallax], .automnia-pricing__card[data-automnia-parallax], .automnia-plugin-fabric__screen[data-automnia-parallax]';
const pointerStates = new WeakMap();

function resetCard(card) {
  card.classList.remove('is-parallax');
  const state = pointerStates.get(card);
  if (state?.frame) cancelAnimationFrame(state.frame);
  pointerStates.delete(card);
  card.style.setProperty('--automnia-tilt-x', '0deg');
  card.style.setProperty('--automnia-tilt-y', '0deg');
  card.style.setProperty('--automnia-shift-x', '0px');
  card.style.setProperty('--automnia-shift-y', '0px');
  card.style.setProperty('--automnia-image-shift-x', '0px');
  card.style.setProperty('--automnia-image-shift-y', '0px');
  card.style.setProperty('--automnia-cover-shift-x', '0px');
  card.style.setProperty('--automnia-cover-shift-y', '0px');
}

function normalizePointer(card, event) {
  const bounds = card.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return { x: 0, y: 0 };

  return {
    x: Math.max(-0.5, Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5)),
    y: Math.max(-0.5, Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5)),
  };
}

function applyPointerPosition(card, x, y) {

  // Tilt.js-style percentage mapping: the pointer establishes a full
  // perspective plane, with the center staying flat and each edge reaching
  // half of the configured max tilt.
  const isHero = card.classList.contains('neon-storefront__agents-banner');
  const maxTilt = isHero ? 9 : 4.5;
  const cardShift = isHero ? 4.5 : 4;
  const imageShift = isHero ? 0 : 10;
  const coverShift = isHero ? 0 : 12;
  const percentageX = x + 0.5;
  const percentageY = y + 0.5;
  const tiltY = (maxTilt / 2) - (percentageX * maxTilt);
  const tiltX = (percentageY * maxTilt) - (maxTilt / 2);

  card.classList.add('is-parallax');
  card.style.setProperty('--automnia-tilt-x', `${tiltX.toFixed(2)}deg`);
  card.style.setProperty('--automnia-tilt-y', `${tiltY.toFixed(2)}deg`);
  card.style.setProperty('--automnia-shift-x', `${(x * cardShift).toFixed(1)}px`);
  card.style.setProperty('--automnia-shift-y', `${(y * cardShift).toFixed(1)}px`);
  card.style.setProperty('--automnia-image-shift-x', `${(x * -imageShift).toFixed(1)}px`);
  card.style.setProperty('--automnia-image-shift-y', `${(y * -imageShift).toFixed(1)}px`);
  card.style.setProperty('--automnia-cover-shift-x', `${(x * coverShift).toFixed(1)}px`);
  card.style.setProperty('--automnia-cover-shift-y', `${(y * coverShift).toFixed(1)}px`);

}

function animateCard(card) {
  const state = pointerStates.get(card);
  if (!state) return;

  // Ease toward the target instead of applying the enter position in one frame.
  // This keeps a fast pointer entry from snapping the plane into a corner tilt.
  const easing = card.classList.contains('neon-storefront__agents-banner') ? 0.08 : 0.12;
  state.current.x += (state.target.x - state.current.x) * easing;
  state.current.y += (state.target.y - state.current.y) * easing;
  applyPointerPosition(card, state.current.x, state.current.y);

  const settled = Math.abs(state.target.x - state.current.x) < 0.001 && Math.abs(state.target.y - state.current.y) < 0.001;
  if (state.active || !settled) {
    state.frame = requestAnimationFrame(() => animateCard(card));
  } else {
    resetCard(card);
  }
}

function startCardAnimation(card) {
  const state = pointerStates.get(card);
  if (!state || state.frame) return;
  state.frame = requestAnimationFrame(() => {
    state.frame = null;
    animateCard(card);
  });
}

function bindCard(card) {
  if (!(card instanceof HTMLElement) || card.dataset.automniaParallaxReady === 'true') return;
  card.dataset.automniaParallaxReady = 'true';

  card.addEventListener('pointerenter', (event) => {
    const state = pointerStates.get(card) || {
      active: true,
      current: { x: 0, y: 0 },
      target: { x: 0, y: 0 },
      frame: null,
    };
    state.active = true;
    state.target = normalizePointer(card, event);
    pointerStates.set(card, state);
    card.classList.add('is-parallax');
    startCardAnimation(card);
  });

  card.addEventListener('pointermove', (event) => {
    const state = pointerStates.get(card);
    if (!state) return;
    state.target = normalizePointer(card, event);
    startCardAnimation(card);
  });

  card.addEventListener('pointerleave', () => {
    const state = pointerStates.get(card);
    if (!state) return resetCard(card);
    state.active = false;
    state.target = { x: 0, y: 0 };
    startCardAnimation(card);
  });
  card.addEventListener('focusout', (event) => {
    if (!card.contains(event.relatedTarget)) resetCard(card);
  });
}

function bindCards(root = document) {
  root.querySelectorAll(cardSelector).forEach(bindCard);
}

function updateParallaxAvailability() {
  const enabled = hoverMedia.matches && !reducedMotionMedia.matches;
  document.documentElement.classList.toggle('automnia-parallax-enabled', enabled);
  if (enabled) bindCards();
  else document.querySelectorAll(cardSelector).forEach(resetCard);
}

if (hoverMedia.matches && !reducedMotionMedia.matches) bindCards();
updateParallaxAvailability();

hoverMedia.addEventListener?.('change', updateParallaxAvailability);
reducedMotionMedia.addEventListener?.('change', updateParallaxAvailability);

const observer = new MutationObserver((mutations) => {
  if (!document.documentElement.classList.contains('automnia-parallax-enabled')) return;
  mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
    if (node instanceof HTMLElement) {
      if (node.matches(cardSelector)) bindCard(node);
      bindCards(node);
    }
  }));
});

observer.observe(document.body, { childList: true, subtree: true });
